import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { clearUser, getUserById } from "../../redux/actions/user.dispatcher";

const PrivateRoute = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userData, loading } = useSelector((state) => state.user);

  // Guard against infinite fetch loops: only attempt one profile fetch per mount.
  // Without this, a failing GET /user/profile (e.g. GHL API down) would cause
  // the useEffect to re-dispatch getUserById() on every re-render because
  // userData stays null and loading flips back to false.
  const fetchAttempted = useRef(false);
  const userDataRef = useRef(userData);
  userDataRef.current = userData;

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      setIsAuthenticated(true);

      // If we have a token but no userData yet (e.g. page refresh),
      // trigger a profile fetch so we can check twoFactorEnabled.
      // Only attempt once to prevent infinite loops if the fetch fails.
      if (!userData && !loading && !fetchAttempted.current) {
        fetchAttempted.current = true;
        dispatch(getUserById());
      }

      // If the profile fetch was attempted but userData is still null
      // (meaning the fetch failed), log the user out to avoid a blank screen.
      if (!userData && !loading && fetchAttempted.current) {
        // Give the reducer a tick to set userData after the fetch resolves.
        // If we're here because the fetch just started (fetchAttempted was
        // set above), userData will arrive on the next render cycle.
        // If userData is still null on a subsequent render, the fetch failed.
        return;
      }

      // Enforce mandatory password change first — it takes priority over
      // 2FA setup because the user may still be on a temporary password.
      if (
        userData &&
        userData.mustChangePassword === true &&
        location.pathname !== "/change-password"
      ) {
        navigate("/change-password");
        return;
      }

      // Enforce mandatory 2FA setup (only after password is changed):
      if (
        userData &&
        !userData.mustChangePassword &&
        userData.twoFactorEnabled === false &&
        !userData.twoFactorExempt &&
        location.pathname !== "/setup-2fa"
      ) {
        navigate("/setup-2fa");
        return;
      }
    } else {
      setIsAuthenticated(false);
      navigate("/login");
    }
  }, [navigate, userData, location.pathname, loading, dispatch]);

  // If the profile fetch failed (attempted but no userData), clear state and
  // redirect to login so the user can try again instead of staring at a blank page.
  useEffect(() => {
    if (fetchAttempted.current && !userData && !loading) {
      // Use a small delay so we don't race with the reducer setting userData.
      // Read from the ref inside the timeout to get the latest value, since
      // the closure would otherwise capture the stale `userData`.
      const timeout = setTimeout(() => {
        if (!userDataRef.current) {
          console.warn("Profile fetch failed after login — redirecting to login.");
          dispatch(clearUser());
          localStorage.removeItem("token");
          navigate("/login");
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [userData, loading, dispatch, navigate]);

  // Don't render protected content until we know the user's 2FA status.
  // This prevents a brief flash of dashboard content before the redirect
  // to /setup-2fa fires on page refresh.
  if (!isAuthenticated) return null;
  if (!userData) return null;

  return <Outlet />;
};

export default PrivateRoute;
